import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const permissionsApi = {
  // Actions
  getAllActions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.published !== undefined) {
        queryParams.append('published', params.published.toString());
      }
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/actions${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch actions', error.status || 500);
    }
  },

  getActionById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/actions/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Action not found', 404);
      }
      throw new ApiError('Failed to fetch action', error.status || 500);
    }
  },

  createAction: async (actionData) => {
    try {
      const { name, description, color } = actionData;
      
      const payload = {
        name: name.trim(),
        ...(description && { description: description.trim() }),
        ...(color && { color: color.trim() })
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/actions`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action data', 400);
      }
      throw new ApiError('Failed to create action', error.status || 500);
    }
  },

  updateAction: async (id, actionData) => {
    try {
      const { name, description, color } = actionData;
      
      const payload = {};
      
      if (name !== undefined) {
        payload.name = name.trim();
      }
      
      if (description !== undefined) {
        payload.description = description.trim();
      }
      
      if (color !== undefined) {
        payload.color = color.trim();
      }
      
      return await authenticatedApi.put(`${API_BASE_URL}/actions/${id}`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Action not found', 404);
      }
      throw new ApiError('Failed to update action', error.status || 500);
    }
  },

  publishAction: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/actions/${id}/publish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Action not found', 404);
      }
      throw new ApiError('Failed to publish action', error.status || 500);
    }
  },

  unpublishAction: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/actions/${id}/unpublish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Action not found', 404);
      }
      throw new ApiError('Failed to unpublish action', error.status || 500);
    }
  },

  deleteAction: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/actions/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Action not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError('Cannot delete action. It has associated permissions.', 409);
      }
      throw new ApiError('Failed to delete action', error.status || 500);
    }
  },

  // Bulk Actions operations
  bulkPublishActions: async (ids) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/actions/bulk/publish`, { ids });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action IDs', 400);
      }
      throw new ApiError('Failed to publish actions', error.status || 500);
    }
  },

  bulkUnpublishActions: async (ids) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/actions/bulk/unpublish`, { ids });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action IDs', 400);
      }
      throw new ApiError('Failed to unpublish actions', error.status || 500);
    }
  },

  bulkDeleteActions: async (ids) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/actions/bulk`, { ids });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action IDs', 400);
      }
      if (error.status === 409) {
        throw new ApiError('Cannot delete some actions. They have associated permissions.', 409);
      }
      throw new ApiError('Failed to delete actions', error.status || 500);
    }
  },

  // Resources
  getAllResources: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.published !== undefined) {
        queryParams.append('published', params.published.toString());
      }
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/resources${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch resources', error.status || 500);
    }
  },

  getResourceById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/resources/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Resource not found', 404);
      }
      throw new ApiError('Failed to fetch resource', error.status || 500);
    }
  },

  createResource: async (resourceData) => {
    try {
      const { name, description } = resourceData;
      
      const payload = {
        name: name.trim(),
        ...(description && { description: description.trim() })
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/resources`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid resource data', 400);
      }
      throw new ApiError('Failed to create resource', error.status || 500);
    }
  },

  updateResource: async (id, resourceData) => {
    try {
      const { name, description } = resourceData;
      
      const payload = {};
      
      if (name !== undefined) {
        payload.name = name.trim();
      }
      
      if (description !== undefined) {
        payload.description = description.trim();
      }
      
      return await authenticatedApi.put(`${API_BASE_URL}/resources/${id}`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid resource data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Resource not found', 404);
      }
      throw new ApiError('Failed to update resource', error.status || 500);
    }
  },

  publishResource: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/resources/${id}/publish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Resource not found', 404);
      }
      throw new ApiError('Failed to publish resource', error.status || 500);
    }
  },

  unpublishResource: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/resources/${id}/unpublish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Resource not found', 404);
      }
      throw new ApiError('Failed to unpublish resource', error.status || 500);
    }
  },

  deleteResource: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/resources/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Resource not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError('Cannot delete resource. It has associated permissions.', 409);
      }
      throw new ApiError('Failed to delete resource', error.status || 500);
    }
  },

  // Roles
  getAllRoles: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.published !== undefined) {
        queryParams.append('published', params.published.toString());
      }
      
      if (params.systemRole !== undefined) {
        queryParams.append('systemRole', params.systemRole.toString());
      }
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/roles${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch roles', error.status || 500);
    }
  },

  getRoleById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/roles/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      throw new ApiError('Failed to fetch role', error.status || 500);
    }
  },

  createRole: async (roleData) => {
    try {
      const { name, displayName, description, hierarchyLevel, icon, color, isSystemRole } = roleData;
      
      const payload = {
        name: name.trim(),
        ...(displayName && { displayName: displayName.trim() }),
        ...(description && { description: description.trim() }),
        ...(hierarchyLevel !== undefined && { hierarchyLevel }),
        ...(icon && { icon: icon.trim() }),
        ...(color && { color: color.trim() }),
        ...(isSystemRole !== undefined && { isSystemRole })
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/roles`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role data', 400);
      }
      throw new ApiError('Failed to create role', error.status || 500);
    }
  },

  updateRole: async (id, roleData) => {
    try {
      const { name, displayName, description, hierarchyLevel, icon, color, isSystemRole } = roleData;
      
      const payload = {};
      
      if (name !== undefined) {
        payload.name = name.trim();
      }
      
      if (displayName !== undefined) {
        payload.displayName = displayName.trim();
      }
      
      if (description !== undefined) {
        payload.description = description.trim();
      }
      
      if (hierarchyLevel !== undefined) {
        payload.hierarchyLevel = hierarchyLevel;
      }
      
      if (icon !== undefined) {
        payload.icon = icon.trim();
      }
      
      if (color !== undefined) {
        payload.color = color.trim();
      }
      
      if (isSystemRole !== undefined) {
        payload.isSystemRole = isSystemRole;
      }
      
      return await authenticatedApi.put(`${API_BASE_URL}/roles/${id}`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      throw new ApiError('Failed to update role', error.status || 500);
    }
  },

  publishRole: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/roles/${id}/publish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      throw new ApiError('Failed to publish role', error.status || 500);
    }
  },

  unpublishRole: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/roles/${id}/unpublish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      throw new ApiError('Failed to unpublish role', error.status || 500);
    }
  },

  deleteRole: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/roles/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError('Cannot delete role. It has associated permissions or employees.', 409);
      }
      throw new ApiError('Failed to delete role', error.status || 500);
    }
  },

  // Employee Access Roles
  getAllEmployeeAccessRoles: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.published !== undefined) {
        queryParams.append('published', params.published.toString());
      }
      
      if (params.employeeId) {
        queryParams.append('employeeId', params.employeeId);
      }
      
      if (params.roleId) {
        queryParams.append('roleId', params.roleId);
      }
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/employee-access-roles${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch employee access roles', error.status || 500);
    }
  },

  getEmployeeAccessRoleById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-access-roles/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee access role not found', 404);
      }
      throw new ApiError('Failed to fetch employee access role', error.status || 500);
    }
  },

  createEmployeeAccessRole: async (employeeAccessRoleData) => {
    try {
      const { employeeId, roleId } = employeeAccessRoleData;
      
      const payload = {
        employeeId: employeeId,
        roleId: roleId
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/employee-access-roles`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee access role data', 400);
      }
      throw new ApiError('Failed to create employee access role', error.status || 500);
    }
  },

  updateEmployeeAccessRole: async (id, employeeAccessRoleData) => {
    try {
      const { employeeId, roleId } = employeeAccessRoleData;
      
      const payload = {};
      
      if (employeeId !== undefined) {
        payload.employeeId = employeeId;
      }
      
      if (roleId !== undefined) {
        payload.roleId = roleId;
      }
      
      return await authenticatedApi.put(`${API_BASE_URL}/employee-access-roles/${id}`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee access role data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee access role not found', 404);
      }
      throw new ApiError('Failed to update employee access role', error.status || 500);
    }
  },

  publishEmployeeAccessRole: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employee-access-roles/${id}/publish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee access role not found', 404);
      }
      throw new ApiError('Failed to publish employee access role', error.status || 500);
    }
  },

  unpublishEmployeeAccessRole: async (id) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employee-access-roles/${id}/unpublish`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee access role not found', 404);
      }
      throw new ApiError('Failed to unpublish employee access role', error.status || 500);
    }
  },

  deleteEmployeeAccessRole: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employee-access-roles/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee access role data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee access role not found', 404);
      }
      throw new ApiError('Failed to delete employee access role', error.status || 500);
    }
  },

  // Permissions
  createPermission: async (permissionData) => {
    try {
      const { resourceId, actionId, conditions, reason } = permissionData;
      
      const payload = {
        resourceId: resourceId,
        actionId: actionId,
        conditions: conditions || {},
        ...(reason && { reason: reason.trim() })
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/permissions`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid permission data', 400);
      }
      throw new ApiError('Failed to create permission', error.status || 500);
    }
  },

  updatePermission: async (id, permissionData) => {
    try {
      const { actionId, conditions, reason } = permissionData;
      
      const payload = {};
      
      if (actionId !== undefined) {
        payload.actionId = actionId;
      }
      
      if (conditions !== undefined) {
        payload.conditions = conditions;
      }
      
      if (reason !== undefined && reason !== null) {
        payload.reason = reason.trim();
      }

      return await authenticatedApi.put(`${API_BASE_URL}/permissions/${id}`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid permission data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Permission not found', 404);
      }
      throw new ApiError('Failed to update permission', error.status || 500);
    }
  },

  deletePermission: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/permissions/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Permission not found', 404);
      }
      throw new ApiError('Failed to delete permission', error.status || 500);
    }
  },

  // Role Permission Assignment
  assignPermissionToRole: async (rolePermissionData) => {
    try {
      const { roleId, permissionId } = rolePermissionData;
      
      const payload = {
        roleId: roleId,
        permissionId: permissionId
      };
      
      return await authenticatedApi.post(`${API_BASE_URL}/roles/assign-permission`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role permission assignment data', 400);
      }
      if (error.status === 409) {
        throw new ApiError('Permission already assigned to role', 409);
      }
      throw new ApiError('Failed to assign permission to role', error.status || 500);
    }
  },

  removePermissionFromRole: async (rolePermissionData) => {
    try {
      const { roleId, permissionId } = rolePermissionData;

      const payload = {
        roleId: roleId,
        permissionId: permissionId
      };

      return await authenticatedApi.post(`${API_BASE_URL}/roles/remove-permission`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role permission removal data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Permission assignment not found', 404);
      }
      throw new ApiError('Failed to remove permission from role', error.status || 500);
    }
  },

  // Role Position Assignment
  assignPositionToRole: async (rolePositionData) => {
    try {
      const { roleId, positionId } = rolePositionData;

      const payload = {
        roleId: roleId,
        positionId: positionId
      };

      return await authenticatedApi.post(`${API_BASE_URL}/roles/assign-position`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role position assignment data', 400);
      }
      if (error.status === 409) {
        throw new ApiError('Position already assigned to role', 409);
      }
      throw new ApiError('Failed to assign position to role', error.status || 500);
    }
  },

  removePositionFromRole: async (rolePositionData) => {
    try {
      const { roleId, positionId } = rolePositionData;

      const payload = {
        roleId: roleId,
        positionId: positionId
      };

      return await authenticatedApi.post(`${API_BASE_URL}/roles/remove-position`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role position removal data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Position assignment not found', 404);
      }
      throw new ApiError('Failed to remove position from role', error.status || 500);
    }
  },

  // Employee Permission Overrides
  getAllEmployeePermissionOverrides: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-permission-overrides`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee permission overrides', error.status || 500);
    }
  },

  getEmployeePermissionOverrideById: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-permission-overrides/employee/${employeeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee permission overrides not found', 404);
      }
      throw new ApiError('Failed to fetch employee permission overrides', error.status || 500);
    }
  },

  assignEmployeePermissionOverride: async (overrideData) => {
    try {
      const { permissionId, employeeId, overrideType, reason } = overrideData;

      const payload = {
        permissionId,
        employeeId,
        overrideType,
        ...(reason && { reason: reason.trim() })
      };

      return await authenticatedApi.post(`${API_BASE_URL}/employee-permission-overrides/assign`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid permission override data', 400);
      }
      if (error.status === 409) {
        throw new ApiError('Permission override already exists for this employee', 409);
      }
      throw new ApiError('Failed to assign permission override', error.status || 500);
    }
  },

  removeEmployeePermissionOverride: async (overrideData) => {
    try {
      const { permissionId, employeeId } = overrideData;

      const payload = {
        permissionId,
        employeeId
      };

      return await authenticatedApi.post(`${API_BASE_URL}/employee-permission-overrides/remove`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid permission override removal data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Permission override not found', 404);
      }
      throw new ApiError('Failed to remove permission override', error.status || 500);
    }
  },

  // Role Position Mapping Grades
  getRolePositionMappingGrades: async (rolePositionMappingId) => {
    try {
      const url = rolePositionMappingId
        ? `${API_BASE_URL}/role-position-mappings/${rolePositionMappingId}/grades`
        : `${API_BASE_URL}/role-position-mapping-grades`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role position mapping not found', 404);
      }
      throw new ApiError('Failed to fetch role position mapping grades', error.status || 500);
    }
  },

  createRolePositionMappingGrade: async (data) => {
    try {
      const { rolePositionMappingId, gradeId } = data;

      const payload = {
        rolePositionMappingId,
        gradeId
      };

      return await authenticatedApi.post(`${API_BASE_URL}/role-position-mapping-grades`, payload);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'rolePositionMappingId and gradeId are required', 400);
      }
      if (error.status === 409) {
        throw new ApiError('This grade is already associated with the role position mapping', 409);
      }
      throw new ApiError('Failed to create role position mapping grade', error.status || 500);
    }
  },

  deleteRolePositionMappingGrade: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/role-position-mapping-grades/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role position mapping grade not found', 404);
      }
      throw new ApiError('Failed to delete role position mapping grade', error.status || 500);
    }
  },

  // Qualifying Employees for a Role
  getQualifyingEmployees: async (roleId, options = {}) => {
    try {
      const { limit = 20, cursor, clusterId, isActive } = options;

      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());

      if (cursor) {
        queryParams.append('cursor', cursor);
      }
      if (clusterId) {
        queryParams.append('clusterId', clusterId);
      }
      if (isActive !== undefined) {
        queryParams.append('isActive', isActive.toString());
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/roles/${roleId}/qualifying-employees?${queryString}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Role not found', 404);
      }
      throw new ApiError('Failed to fetch qualifying employees', error.status || 500);
    }
  }
};